import { useEffect, useState } from "react";
import type { AgentDTO, ConnectorDTO, SubjectTypeDTO } from "../../../shared/types";
import { List, ListRow } from "../../components/List";
import { confirmDialog, Dialog } from "../../components/Dialog";
import { ScopeList } from "../../components/ScopeList";
import { ToolMark, TypeForm, typeReady, type TypeDraft } from "../../components/ScopeTypeForm";
import { TextLink } from "../../components/TextLink";
import { AgentAvatar } from "../../components/AgentAvatar";
import { Badge, Button, ErrorText, inputCls } from "../../components/ui";
import { ApiError, del, get, patch, post, put } from "../../lib/api";
import { navigate } from "../../lib/router";
import { toolName } from "../../lib/scopes";

/** Who may work under, and create, scopes of this type. The primary agent works for people and never under a scope. */
function AccessPanel({ type, agents, onChanged }: { type: SubjectTypeDTO; agents: AgentDTO[]; onChanged: (type: SubjectTypeDTO) => void }) {
  const [adding, setAdding] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const withAccess = agents.filter((a) => type.agentIds.includes(a.id));
  // An agent works per one scope type (094): only the ones with none yet can be given this one.
  const others = agents.filter((a) => !a.isPrimary && !a.archivedAt && a.scopeTypes.length === 0);
  const access = async (agentId: string, allowed: boolean) => {
    setBusy(true);
    setError(null);
    try {
      const path = `/api/subject-types/${encodeURIComponent(type.key)}/agents/${agentId}`;
      onChanged(await (allowed ? put<SubjectTypeDTO>(path, {}) : del<SubjectTypeDTO>(path)));
      setAdding("");
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <List
      title="Agents with access"
      hint={`Only these agents work under, and may create, ${type.name.toLowerCase()} scopes.`}
      empty="No agent has access yet."
      footer={
        <>
          <div className="flex gap-2">
            <select className={`${inputCls} min-w-0`} value={adding} onChange={(e) => setAdding(e.target.value)} aria-label="Agent to give access">
              <option value="">{others.length ? "Give an agent access…" : "Every agent already works per a scope type"}</option>
              {others.map((a) => (
                <option key={a.id} value={a.id}>{a.name}</option>
              ))}
            </select>
            <Button disabled={busy || !adding} onClick={() => access(adding, true)}>Add</Button>
          </div>
          {error && (
            <div className="mt-2">
              <ErrorText error={error} />
            </div>
          )}
        </>
      }
    >
      {withAccess.map((a) => (
        <ListRow
          key={a.id}
          leading={<AgentAvatar agent={a.id} name={a.name} size="sm" />}
          title={<TextLink to={`/admin/agents/${a.id}`} quiet className="truncate text-sm font-medium">{a.name}</TextLink>}
          trailing={
            <Button
              variant="ghost"
              size="sm"
              disabled={busy}
              onClick={async () =>
                (await confirmDialog({ title: `Take ${type.name} away from ${a.name}?`, body: `It stops working under ${type.name.toLowerCase()} scopes.`, confirm: "Remove", danger: true })) && access(a.id, false)
              }
            >
              Remove
            </Button>
          }
        />
      ))}
    </List>
  );
}

function ToolsPanel({ type, connectors, onEdit }: { type: SubjectTypeDTO; connectors: ConnectorDTO[]; onEdit: () => void }) {
  return (
    <List
      title="Tools"
      hint="The tools its scopes have an id in, and what that id is called there."
      action={<Button variant="ghost" size="sm" onClick={onEdit}>Edit</Button>}
      empty="No tools yet, so its scopes hold a name only."
    >
      {type.fields.map((f, i) => (
        <ListRow
          key={f.system}
          leading={
            <span className="grid size-8 place-items-center rounded-full bg-raised shadow-sm ring-1 ring-line">
              <ToolMark system={f.system} connectors={connectors} />
            </span>
          }
          title={<span className="truncate text-sm font-medium">{toolName(connectors, f.system)}</span>}
          subtitle={<span className="font-mono text-xs">{f.idField}</span>}
          trailing={i === 0 && <Badge tone="blue">Main tool</Badge>}
        />
      ))}
    </List>
  );
}

/** Edits the name, description and tools; a type with no scopes can also be deleted here. */
const ARCHIVE_TYPE_BODY = "It and all its scopes are kept, with everything in them, and hidden. No new scope of it is made and no new work starts under its scopes. You can unarchive it.";

function EditTypeModal({ type, connectors, onClose, onSaved }: { type: SubjectTypeDTO; connectors: ConnectorDTO[]; onClose: () => void; onSaved: (type: SubjectTypeDTO) => void }) {
  const [draft, setDraft] = useState<TypeDraft>({ key: type.key, name: type.name, pluralName: type.pluralName, description: type.description, fields: type.fields });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      open
      onClose={onClose}
      size="lg"
      title={`Edit ${type.name}`}
      busy={busy}
      secondary={
        <Button
          variant="danger"
          disabled={busy}
          onClick={async () =>
            (await confirmDialog({
              title: `Delete the scope type ${type.name}?`,
              body: "Only a type none of whose scopes has ever run can be deleted. Its scopes, with what was kept on them, go with it, its agents lose their access, and it cannot be undone.",
              confirm: "Delete type",
              danger: true,
            })) &&
            run(async () => {
              try {
                await del(`/api/subject-types/${encodeURIComponent(type.key)}`);
                navigate("/admin/scopes");
              } catch (err) {
                // Its scopes have run: say so, and offer the way that keeps them.
                if (!(err instanceof ApiError) || err.code !== "has_runs" || type.archivedAt) throw err;
                const offer = await confirmDialog({ title: `${type.name} can't be deleted`, body: `${err.message.replace(/: archive it instead$/, "")}. Archive it instead: ${ARCHIVE_TYPE_BODY}`, confirm: "Archive instead" });
                if (offer) onSaved(await post<SubjectTypeDTO>(`/api/subject-types/${encodeURIComponent(type.key)}/archive`));
              }
            })
          }
        >
          Delete type
        </Button>
      }
      primary={
        <Button
          variant="primary"
          disabled={busy || !typeReady(draft)}
          onClick={() =>
            run(async () => {
              const fields = draft.fields.map((f) => ({ system: f.system, idField: f.idField.trim() }));
              onSaved(await patch<SubjectTypeDTO>(`/api/subject-types/${encodeURIComponent(type.key)}`, { name: draft.name.trim(), pluralName: draft.pluralName.trim(), description: draft.description.trim(), fields }));
            })
          }
        >
          {busy ? "Saving…" : "Save"}
        </Button>
      }
    >
      <TypeForm draft={draft} connectors={connectors} isNew={false} onChange={setDraft} />
      <div className="mt-3">
        <ErrorText error={error} />
      </div>
    </Dialog>
  );
}

/** One scope type: its tools, the agents with access to it, and its scopes. */
export function AdminScopeType({ typeKey }: { typeKey: string }) {
  const [type, setType] = useState<SubjectTypeDTO | null>(null);
  const [agents, setAgents] = useState<AgentDTO[]>([]);
  const [connectors, setConnectors] = useState<ConnectorDTO[]>([]);
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setType(null);
    setError(null);
    get<SubjectTypeDTO>(`/api/subject-types/${encodeURIComponent(typeKey)}`).then(setType, (e) => setError(e.message));
    get<AgentDTO[]>("/api/agents").then(setAgents, () => {});
    get<ConnectorDTO[]>("/api/connectors").then(setConnectors, () => {});
  }, [typeKey]);

  if (!type) {
    return (
      <div className="space-y-4 px-4 py-8 md:px-6">
        {error ? <ErrorText error={error} /> : <div className="card h-40 animate-pulse" />}
      </div>
    );
  }
  return (
    <div className="pb-12">
      <div className="px-4 pt-5 md:px-6">
        <TextLink to="/admin/scopes" quiet className="text-sm text-muted">← Scopes</TextLink>
      </div>
      <div className="flex flex-col gap-3 px-4 pt-4 pb-5 sm:flex-row sm:items-end sm:justify-between md:px-6">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="truncate text-title font-semibold tracking-tight">{type.name}</h1>
            <Badge>Scope type</Badge>
            {type.archivedAt && <Badge tone="amber">Archived</Badge>}
          </div>
          <p className="mt-1 font-mono text-sm text-muted">{type.key}:&lt;id&gt;</p>
          {type.description && <p className="mt-1.5 max-w-3xl text-sm text-muted">{type.description}</p>}
          <ErrorText error={error} />
        </div>
        <div className="flex gap-2 self-start sm:self-auto">
          <Button
            onClick={async () => {
              setError(null);
              try {
                if (type.archivedAt) setType(await post<SubjectTypeDTO>(`/api/subject-types/${encodeURIComponent(type.key)}/unarchive`));
                else if (await confirmDialog({ title: `Archive ${type.name}?`, body: ARCHIVE_TYPE_BODY, confirm: "Archive" })) {
                  setType(await post<SubjectTypeDTO>(`/api/subject-types/${encodeURIComponent(type.key)}/archive`));
                }
              } catch (err) {
                setError((err as Error).message);
              }
            }}
          >
            {type.archivedAt ? "Unarchive" : "Archive"}
          </Button>
          <Button onClick={() => setEditing(true)}>Edit type</Button>
        </div>
      </div>
      <div className="grid grid-cols-1 items-start gap-4 px-4 md:px-6 lg:grid-cols-[minmax(0,1fr)_22rem]">
        <ScopeList type={type} connectors={connectors} />
        {/* On a phone the short panels come first, before a list that can run to hundreds. */}
        <div className="order-first min-w-0 space-y-4 lg:order-none">
          <ToolsPanel type={type} connectors={connectors} onEdit={() => setEditing(true)} />
          <AccessPanel type={type} agents={agents} onChanged={setType} />
        </div>
      </div>
      {editing && (
        <EditTypeModal
          type={type}
          connectors={connectors}
          onClose={() => setEditing(false)}
          onSaved={(next) => {
            setType(next);
            setEditing(false);
          }}
        />
      )}
    </div>
  );
}
